import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Search, Sliders, X } from 'lucide-react';
import Header from '../components/layout/Header';
import DesktopSidebar from '../components/layout/DesktopSidebar';
import ProductGrid from './home/ProductGrid';
import { getListings, getCategories } from '../services/api';
import Footer from '../components/layout/Footer';
import MobileBottomNav from '../components/layout/MobileBottomNav';
import Loading from '../components/common/Loading';

const SearchPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
   
  const query = searchParams.get('q') || '';
  const categoryId = searchParams.get('category') || null;
  const locationId = searchParams.get('location') || null;
  
  const [listings, setListings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(query);
  const [showFilters, setShowFilters] = useState(false);
  
  const [filters, setFilters] = useState({
    category: categoryId,
    minPrice: null,
    maxPrice: null,
    condition: 'all',
    location: locationId,
  });

  // State for ProductGrid controls
  const [gridControls, setGridControls] = useState({
    viewMode: 'grid',
    sortBy: 'relevance',
    currentPage: 1
  });

  useEffect(() => {
    fetchData();
  }, [query, categoryId, locationId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const apiFilters = {
        search: query,
        category_id: categoryId,
        state_id: locationId,
        status: 'active',
        moderation_status: 'approved'
      };

      const [listingsResponse, categoriesResponse] = await Promise.all([
        getListings(apiFilters),
        getCategories()
      ]);

      setListings(listingsResponse.data || listingsResponse || []);
      setCategories(categoriesResponse.data || categoriesResponse || []);
      
    } catch (error) {
      console.error('Error fetching search results:', error);
      setListings([]);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const handleFilterChange = (newFilters) => {
    const updatedFilters = { ...filters, ...newFilters };
    setFilters(updatedFilters);
    
    // Reset to first page when filters change
    setGridControls(prev => ({ ...prev, currentPage: 1 }));
    
    // Update URL with filters
    const params = new URLSearchParams();
    if (searchQuery) params.set('q', searchQuery);
    if (updatedFilters.category) params.set('category', updatedFilters.category);
    if (updatedFilters.location) params.set('location', updatedFilters.location);
    
    navigate(`/search?${params.toString()}`);
  };

  const clearFilters = () => {
    setFilters({
      category: null,
      minPrice: null,
      maxPrice: null,
      condition: 'all',
      location: null,
    });
    setSearchQuery('');
    setGridControls(prev => ({ ...prev, currentPage: 1 }));
    navigate('/search');
  };

  // Filter listings
  const filteredListings = listings.filter(listing => {
    if (filters.category && listing.category_id !== filters.category) return false;
    if (filters.condition !== 'all' && listing.condition !== filters.condition) return false;
    if (filters.minPrice && parseFloat(listing.price) < filters.minPrice) return false;
    if (filters.maxPrice && parseFloat(listing.price) > filters.maxPrice) return false;
    if (filters.location && listing.state_id !== filters.location) return false;
    return true;
  });

  // Handle ProductGrid control changes
  const handleGridControlChange = (newControls) => {
    setGridControls(prev => ({ ...prev, ...newControls }));
  };

  const hasActiveFilters = 
    filters.category !== null ||
    filters.minPrice !== null ||
    filters.maxPrice !== null ||
    filters.condition !== 'all' ||
    filters.location !== null;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f5f5f5]">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-6">
          <Loading 
            text={`Searching for "${query || "products"}..."`} 
            size="large" 
            color="#ff660e" 
          />
        </div>
      </div>
    );
  }

  // Render empty state
  const renderEmptyState = () => (
    <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12 text-center">
      <div className="w-20 h-20 mx-auto mb-6 bg-[#fff5e6] rounded-full flex items-center justify-center">
        <Search className="w-10 h-10 text-[#ff660e]" />
      </div>
      <h3 className="text-xl font-semibold text-gray-900 mb-2">
        No results found
      </h3>
      <p className="text-gray-600 max-w-md mx-auto mb-6">
        {query 
          ? `We couldn't find any products matching "${query}". Try different keywords or filters.`
          : 'No products available with the current filters.'
        }
      </p>
      {query && (
        <div className="mb-6">
          <p className="text-gray-600 font-medium mb-2">Suggestions:</p>
          <ul className="text-gray-600 space-y-1 text-sm">
            <li>• Check your spelling</li>
            <li>• Try more general keywords</li>
            <li>• Browse categories instead</li>
          </ul>
        </div>
      )}
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <button
          onClick={clearFilters}
          className="px-6 py-3 bg-[#ff660e] text-white font-medium rounded-lg hover:bg-[#e55a0d] transition-colors"
        >
          {query ? 'Clear Search' : 'Clear All Filters'}
        </button>
        <button
          onClick={() => navigate('/categories')}
          className="px-6 py-3 bg-white text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors border border-gray-200"
        >
          Browse Categories
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f5f5f5]">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Search Header */}
        <div className="mb-6 bg-white rounded-xl shadow-sm p-4 md:p-6">
          <form onSubmit={handleSearch} className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search products, brands, and categories..."
              className="w-full pl-5 pr-12 py-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-[#ff660e] focus:border-transparent"
            />
            <button
              type="submit"
              className="absolute right-3 top-1/2 transform -translate-y-1/2 bg-[#ff660e] text-white p-2 rounded-full hover:bg-[#e55a0d]"
            >
              <Search className="w-5 h-5" />
            </button>
          </form>
          
          <div className="mt-4 flex flex-wrap items-center justify-between">
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-gray-900">
                {query ? `Results for "${query}"` : 'Search Products'}
              </h1>
              <p className="text-gray-600">
                Found {filteredListings.length} {filteredListings.length === 1 ? 'item' : 'items'}
              </p>
            </div>
            
            <div className="flex items-center space-x-3 mt-4 md:mt-0">
              {/* Mobile Filter Toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="lg:hidden flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium"
              >
                <Sliders className="w-4 h-4 mr-2" />
                {showFilters ? 'Hide' : 'Filter'}
              </button>
            </div>
          </div>
          
          {/* Active Filters */}
          {hasActiveFilters && (
            <div className="mt-4 pt-4 border-t">
              <div className="flex flex-wrap gap-2">
                {filters.category && (
                  <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm bg-[#fff5e6] text-[#ff660e] border border-[#ffd9b3]">
                    Category: {categories.find(c => c.id === filters.category)?.name}
                    <button
                      onClick={() => handleFilterChange({ category: null })}
                      className="ml-2 hover:text-[#e55a0d]"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
                {filters.condition !== 'all' && (
                  <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm bg-green-100 text-green-800 border border-green-200">
                    Condition: {filters.condition}
                    <button
                      onClick={() => handleFilterChange({ condition: 'all' })}
                      className="ml-2 hover:text-green-900"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
                {filters.location && (
                  <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm bg-purple-100 text-purple-800 border border-purple-200">
                    Location
                    <button
                      onClick={() => handleFilterChange({ location: null })}
                      className="ml-2 hover:text-purple-900"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
                {(filters.minPrice || filters.maxPrice) && (
                  <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm bg-yellow-100 text-yellow-800 border border-yellow-200">
                    Price Range
                    <button
                      onClick={() => handleFilterChange({ minPrice: null, maxPrice: null })}
                      className="ml-2 hover:text-yellow-900"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
                <button
                  onClick={clearFilters}
                  className="text-sm text-[#ff660e] hover:text-[#e55a0d] font-medium"
                >
                  Clear All Filters
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-6">
          {/* Desktop Sidebar - Filters */}
          <div className="hidden lg:block w-72 flex-shrink-0">
            <div className="sticky top-24 h-[calc(100vh-96px)]">
              <DesktopSidebar 
                categories={categories}
                filters={filters}
                onFilterChange={handleFilterChange}
                listings={filteredListings}
              />
            </div>
          </div>

          {/* Mobile Filters Overlay */}
          {showFilters && (
            <div className="lg:hidden fixed inset-0 bg-black/50 z-50">
              <div className="absolute right-0 top-0 h-full w-80 bg-white shadow-xl overflow-y-auto">
                <div className="p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-gray-900">Filters</h3>
                    <button 
                      onClick={() => setShowFilters(false)}
                      className="p-2 hover:bg-gray-100 rounded-lg"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <DesktopSidebar 
                    categories={categories}
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    listings={filteredListings}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Use ProductGrid with all controls */}
            {filteredListings.length > 0 ? (
              <ProductGrid 
                listings={filteredListings}
                viewMode={gridControls.viewMode}
                sortBy={gridControls.sortBy}
                currentPage={gridControls.currentPage}
                onViewModeChange={(mode) => handleGridControlChange({ viewMode: mode })}
                onSortChange={(sortBy) => handleGridControlChange({ sortBy, currentPage: 1 })}
                onPageChange={(page) => handleGridControlChange({ currentPage: page })}
                showControls={true}
                isSearchPage={true}
              />
            ) : (
              renderEmptyState()
            )}
          </div>
        </div>
      </div>

      <Footer />
      <MobileBottomNav />
    </div>
  );
};

export default SearchPage;